import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

// Same palette and hash as AssignedUsersBubbles, so one person is the same
// color here and on the contratos.
const AVATAR_COLORS = [
  'bg-azul-surelia/20 text-azul-surelia',
  'bg-verde-lima/30 text-azul-surelia',
  'bg-cian-digital/20 text-cian-digital',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
];

export const colorDe = (uid) => {
  if (!uid) return AVATAR_COLORS[0];
  const hash = uid.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

export const inicialesDe = (m) => {
  const base = (m?.nombre || m?.correo || '').trim();
  if (!base) return '?';
  const partes = base.split(/\s+/).filter(Boolean);
  if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

const SIZES = {
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-7 h-7 text-[11px]',
};

/**
 * The assignees of a pendiente as stacked avatar bubbles, editable in place:
 * click opens the member list with checkboxes, tick and untick, done. Several
 * people can carry one pendiente, same as assignedUsers on a cliente.
 *
 * @param {Object} props
 * @param {Array<string>} props.uids  - current assignees
 * @param {Array<{id:string,nombre?:string,correo?:string}>} props.miembros
 * @param {function(Array<string>): void} [props.onChange]  - omit for read-only
 * @param {boolean} [props.disabled]
 * @param {string} [props.meUid]  - marks "(yo)" in the list
 * @param {'sm'|'md'} [props.size]
 * @param {number} [props.max]  - bubbles shown before "+N"
 */
export function AsignadosBubbles({
  uids = [], miembros = [], onChange, disabled = false, meUid = null, size = 'md', max = 3,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const cerrar = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', cerrar);
    return () => document.removeEventListener('mousedown', cerrar);
  }, [open]);

  const porId = {};
  miembros.forEach((m) => { porId[m.id] = m; });
  const nombre = (uid) => (porId[uid] ? (porId[uid].nombre || porId[uid].correo) : uid);
  const editable = Boolean(onChange) && !disabled;
  const lista = Array.isArray(uids) ? uids : [];
  const visibles = lista.slice(0, max);
  const extra = lista.length - visibles.length;
  const sz = SIZES[size] || SIZES.md;

  const toggle = (uid) => {
    const next = lista.includes(uid) ? lista.filter((u) => u !== uid) : [...lista, uid];
    onChange(next);
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        title={editable
          ? `${lista.length ? lista.map(nombre).join(', ') : 'Sin asignar'} · clic para asignar`
          : (lista.length ? lista.map(nombre).join(', ') : 'Sin asignar')}
        disabled={!editable}
        onClick={() => editable && setOpen((v) => !v)}
        className={clsx('flex items-center -space-x-1.5', editable ? 'cursor-pointer' : 'cursor-default')}
      >
        {visibles.map((uid) => (
          <span
            key={uid}
            className={clsx(
              'rounded-full flex items-center justify-center font-semibold border-2 border-white shadow-sm',
              sz, colorDe(uid), editable && 'hover:ring-2 hover:ring-azul-surelia/30'
            )}
          >
            {inicialesDe(porId[uid])}
          </span>
        ))}
        {extra > 0 && (
          <span className={clsx('rounded-full flex items-center justify-center font-semibold border-2 border-white bg-zinc-100 text-zinc-600', sz)}>
            +{extra}
          </span>
        )}
        {(lista.length === 0 || editable) && (
          <span className={clsx(
            'rounded-full flex items-center justify-center border-2 border-dashed border-zinc-300 text-zinc-300',
            sz, editable && 'hover:border-azul-surelia hover:text-azul-surelia',
            lista.length > 0 && 'bg-white'
          )}>
            +
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 w-60 rounded-lg border border-zinc-200 bg-white shadow-lg py-1 max-h-64 overflow-y-auto">
          {miembros.map((m) => {
            const marcado = lista.includes(m.id);
            return (
              <label
                key={m.id}
                className={clsx(
                  'w-full flex items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-zinc-50 cursor-pointer',
                  marcado && 'bg-azul-surelia/5'
                )}
              >
                <input
                  type="checkbox"
                  checked={marcado}
                  onChange={() => toggle(m.id)}
                  className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                />
                <span className={clsx(
                  'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0',
                  colorDe(m.id)
                )}>
                  {inicialesDe(m)}
                </span>
                <span className="truncate">
                  {m.nombre || m.correo}{m.id === meUid ? ' (yo)' : ''}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AsignadosBubbles;
